import   React                   ,
       { useEffect               ,
         useMemo                 ,
         useState                }  from "react";

import   classNames                 from 'classnames';

import   styles                     from './BetaApplicationList.module.scss';

import   Field                      from '../../widget/inputs/Field';
import   Select                     from '../../widget/inputs/Select';
import   AnimatedWaitingIcon2       from '../../widget/animations/animatedWaitingIcon2';
import   ColumnTitle                from '../../widget/dataDisplay/labels/ColumnTitle'
import   Window                     from '../../widget/windows/window';
import   Icon                       from '../../widget/images/Icon';
import   BetaApplicationSheet       from './BetaApplicationSheet';

import { BetaApplication                ,
         BetaApplicationStatus          ,
         BETA_APPLICATION_STATUS_VALUES ,
         BETA_APPLICATION_STATUS_LABELS ,
         getBetaActivityLabel           }  from '../../../dataTypes/betaApplicationManagment/types';
import { getBetaApplications            ,
         acceptBetaApplication          ,
         refuseBetaApplication          ,
         waitlistBetaApplication        ,
         BetaApplicationDecision        }  from '../../../backendServices/betaApplicationManagment/api';
import { formatDateOrDash               }  from '../../../dataTypes/adminManagment/utils';

import { useTypedSelector               }  from '../../../store/useTypeSelector';
import { RootState                      }  from '../../../store/generalReducer';

const STATUS_CLASS: Record<BetaApplicationStatus, string> = {
  pending  : styles.status_pending  ,
  waitlist : styles.status_waitlist ,
  accepted : styles.status_accepted ,
  refused  : styles.status_refused  ,
};

const DECISION_TEXT: Record<BetaApplicationDecision, (name: string) => string> = {
  accept   : (name) => `Accepter la candidature de ${name} ? Son compte créateur sera créé et un e-mail contenant son mot de passe lui sera envoyé.` ,
  refuse   : (name) => `Refuser la candidature de ${name} ? Aucun e-mail ne lui sera envoyé.` ,
  waitlist : (name) => `Mettre la candidature de ${name} en liste d'attente ?` ,
};

const DECISION_DONE: Record<BetaApplicationDecision, string> = {
  accept   : 'La candidature a été acceptée : le compte créateur est créé.' ,
  refuse   : 'La candidature a été refusée.' ,
  waitlist : 'La candidature a été mise en liste d\'attente.' ,
};

// Écran « Bêta-testeur » du super-admin : candidatures déposées depuis le formulaire public.
// Les décisions passent par l'API (accepter crée le compte créateur) ; la fiche s'ouvre
// à la place de la liste, comme celle d'un créateur.
export const BetaApplicationList = () => {

  const { authentication } = useTypedSelector((state: RootState) => state.authenticationReducer);

  const [ applications     , setApplications     ] = useState<BetaApplication[]>([]);
  const [ showWaitingIcon  , setShowWaitingIcon  ] = useState(false);

  const [ filter           , setFilter           ] = useState('');
  // Par défaut : ce qui attend une décision
  const [ statusFilter     , setStatusFilter     ] = useState<string>('pending');

  const [ viewId           , setViewId           ] = useState<number | null>(null);

  const [ pendingDecision  , setPendingDecision  ] = useState<{ decision: BetaApplicationDecision , application: BetaApplication } | null>(null);

  const [ message          , setMessage          ] = useState<string | null>(null);

  const load = async () => {

    setShowWaitingIcon(true);

    try
     {
      const result = await getBetaApplications();

      if ( result.code === 1 ) { setApplications ( result.payload ); }
      else                     { setMessage ( `Impossible de charger les candidatures${result.comment ? ` : ${result.comment}` : ''}` ); }
     }
    catch (error)
     {
      console.error('Error fetching beta applications:', error);
      setMessage ( 'Impossible de charger les candidatures' );
     }
    finally
     {
      setShowWaitingIcon(false);
     }
  };

  useEffect(() => { load(); }, []);

  const displayedApplications = useMemo(() => {

    const search = filter.trim().toUpperCase();

    return applications.filter ( (application) =>
      ( statusFilter === '' || application.status === statusFilter ) &&
      (
       search === '' ||
       `${application.firstname} ${application.lastname}`.toUpperCase().includes ( search ) ||
       application.email.toUpperCase().includes ( search )                                  ||
       application.organization.toUpperCase().includes ( search )                           ||
       application.publicName.toUpperCase().includes ( search )
      )
    );

  }, [ applications , filter , statusFilter ]);

  const counts = useMemo(() => {

    const result: Record<string, number> = {};

    applications.forEach ( (application) => { result [ application.status ] = ( result [ application.status ] ?? 0 ) + 1; } );

    return result;

  }, [ applications ]);

  const askDecision = ( decision: BetaApplicationDecision , application: BetaApplication ) => {
    setPendingDecision ({ decision , application });
  };

  const handleDecisionConfirmation = async ( confirmed: boolean ) => {

    const current = pendingDecision;

    setPendingDecision(null);

    if ( !confirmed || current === null ) { return; }

    setShowWaitingIcon(true);

    const action = current.decision === 'accept'  ? acceptBetaApplication
                 : current.decision === 'refuse'  ? refuseBetaApplication
                 :                                  waitlistBetaApplication;

    const result = await action ( current.application.id , authentication.admin?.id );

    setShowWaitingIcon(false);

    if ( result.code === 1 )
          {
           setMessage ( DECISION_DONE [ current.decision ] );
           setViewId ( null );
          }
     else {
           // 409 : déjà traitée, ou e-mail devenu pris — le motif vient de l'API
           setMessage ( result.comment !== '' ? result.comment : 'La décision n\'a pas pu être enregistrée.' );
          }

    await load();
  };

  const viewedApplication = viewId !== null ? applications.find ( (application) => application.id === viewId ) ?? null : null;

  const renderActions = ( application: BetaApplication ) => {

    const open = application.status === 'pending' || application.status === 'waitlist';

    // L'acceptation envoie le mot de passe à l'adresse du candidat : elle n'est possible
    // qu'une fois cette adresse confirmée (l'API le refuse sinon).
    const canAccept = open && application.emailValidated;

    return (
      <div className={classNames(styles.actions)}>

        <div className={styles.iconButton} title="Voir la fiche" onClick={() => setViewId(application.id)}>
          <Icon name="Eye" color="var(--color-Icon-FG)" hoverColor="var(--color-Icon-hover-FG)" width={22} height={22} />
        </div>

        {
         canAccept &&
         <div className={styles.iconButton} title="Accepter" onClick={() => askDecision('accept', application)}>
           <Icon name="Check" color="var(--color-OK)" hoverColor="var(--color-Icon-hover-FG)" width={22} height={22} />
         </div>
        }

        {
         application.status === 'pending' &&
         <div className={styles.iconButton} title="Mettre en liste d'attente" onClick={() => askDecision('waitlist', application)}>
           <Icon name="Clock" color="var(--color-Icon-FG)" hoverColor="var(--color-Icon-hover-FG)" width={22} height={22} />
         </div>
        }

        {
         open &&
         <div className={styles.iconButton} title="Refuser" onClick={() => askDecision('refuse', application)}>
           <Icon name="X" color="var(--color-NOK)" hoverColor="var(--color-Icon-hover-FG)" width={22} height={22} />
         </div>
        }

      </div>
    );
  };

  return (
    <div className={classNames(styles.root)}>

      {showWaitingIcon && <AnimatedWaitingIcon2 />}

      {
        message !== null &&
        <Window message                 = {<>{message}</>}
                messageType             = "information"
                onClickOnValidateButton = {() => setMessage(null)}
                validButtonText         = "Ok" />
      }

      {
        pendingDecision !== null &&
        <Window message                 = {<>{
                                       pendingDecision.decision === 'accept' && pendingDecision.application.isPromotion
                                       ? `Accepter la candidature de ${`${pendingDecision.application.firstname} ${pendingDecision.application.lastname}`.trim()} ? PROMOTION : cette personne a déjà un compte Kleon (n° ${pendingDecision.application.existingUserId}). Il devient compte créateur ; son mot de passe ne change pas et un e-mail lui annonce simplement son accès créateur.`
                                       : DECISION_TEXT[pendingDecision.decision](`${pendingDecision.application.firstname} ${pendingDecision.application.lastname}`.trim())
                                     }</>}
                messageType             = "question"
                onClickOnValidateButton = {() => handleDecisionConfirmation(true)}
                onClickOnCancelButton   = {() => handleDecisionConfirmation(false)}
                validButtonText         = "Oui"
                cancelButtonText        = "Non"
                width                   = "700px" />
      }

      {
       viewedApplication !== null
       ? <BetaApplicationSheet application = {viewedApplication}
                               onClose     = {() => setViewId(null)}
                               onAccept    = {(application) => askDecision('accept', application)}
                               onRefuse    = {(application) => askDecision('refuse', application)}
                               onWaitlist  = {(application) => askDecision('waitlist', application)} />
       : (
        <div className={classNames(styles.frame)}>

          <div className={classNames(styles.head)}>
            <div className={classNames(styles.headTitleBlock)}>
              <span className={classNames(styles.headTitle)}>Bêta-testeurs</span>
              <span className={classNames(styles.headSubtitle)}>
                Candidatures reçues depuis le formulaire public
              </span>
            </div>
          </div>

          <div className={classNames(styles.upper_bar)}>

            <Field type        = "text"
                   placeholder = "Rechercher..."
                   value       = {filter}
                   width       = "280px"
                   onChange    = {(e: React.ChangeEvent<HTMLInputElement>) => setFilter(e.target.value)} />

            <Select choices         = {[ '' , ...BETA_APPLICATION_STATUS_VALUES ]}
                    choicesLabel    = {[ 'Tous les statuts' , ...BETA_APPLICATION_STATUS_VALUES.map ( status => `${BETA_APPLICATION_STATUS_LABELS[status]} (${counts[status] ?? 0})` ) ]}
                    value           = {statusFilter}
                    showPlaceholder = {false}
                    width           = "230px"
                    onChange        = {(e: React.ChangeEvent<HTMLSelectElement>) => setStatusFilter(e.target.value)} />

            <span className={classNames(styles.counter)}>
              {`${displayedApplications.length} candidature${displayedApplications.length > 1 ? 's' : ''}`}
            </span>

          </div>

          <div className={classNames(styles.list_container)}>

            <div className={classNames(styles.list)}>

              <ColumnTitle titles = {[ 'Candidat' , 'Activité' , 'Organisation' , 'Date'  , 'Statut' , 'Action' ]}
                           widths = {[ '320px'    , '200px'    , 'auto'         , '170px'  , '150px'  , '190px'  ]} />

              {
               displayedApplications.length === 0 && !showWaitingIcon &&
               <div className={classNames(styles.empty)}>Aucune candidature</div>
              }

              {
               displayedApplications.map ( (application) => (
                 <div key={application.id} className={classNames(styles.row)}>

                   <div className={classNames(styles.cell)}>
                     <div className={classNames(styles.twoLines)}>
                       <span className={classNames(styles.primaryText)}>{`${application.firstname} ${application.lastname}`.trim() || '—'}</span>
                       {application.publicName && <span className={classNames(styles.secondaryText)} title="Nom public choisi pour le futur compte créateur">« {application.publicName} »</span>}
                       <span className={classNames(styles.secondaryText)}>
                         {application.email}
                         {' — '}
                         <span className={classNames(application.emailValidated ? styles.emailOk : styles.emailKo)}>
                           {application.emailValidated ? 'e-mail vérifié' : 'e-mail non vérifié'}
                         </span>
                         {
                          application.isPromotion &&
                          <span className={classNames(styles.promotionBadge)} title="Cette personne a déjà un compte Kleon : l'acceptation le promeut en créateur">
                            {' '}Promotion
                          </span>
                         }
                       </span>
                     </div>
                   </div>

                   <div className={classNames(styles.cell)}>
                     <span className={classNames(styles.primaryText)}>{getBetaActivityLabel(application)}</span>
                   </div>

                   <div className={classNames(styles.cell)}>
                     <span className={classNames(styles.primaryText)}>{application.organization || '—'}</span>
                   </div>

                   <div className={classNames(styles.cell, styles.cellCenter)}>
                     {formatDateOrDash(application.creationDate)}
                   </div>

                   <div className={classNames(styles.cell, styles.cellCenter, STATUS_CLASS[application.status])}>
                     <span className={classNames(styles.statusDot)} />
                     <span className={classNames(styles.statusLabel)}>{BETA_APPLICATION_STATUS_LABELS[application.status] ?? application.status}</span>
                   </div>

                   <div className={classNames(styles.cell, styles.cellCenter)}>
                     {renderActions(application)}
                   </div>

                 </div>
               ))
              }

            </div>

          </div>

        </div>
       )
      }

    </div>
  );
};

export default BetaApplicationList;
